import { GestureSource } from "./gestures";

/** What the hub's gestures move: the camera's pinch, drag pan and flick (SceneRig has them; tests use a fake). */
export interface HubCameraControl {
  onPinch(scale: number, clientX: number, clientY: number): void;
  dragBegin(clientX: number, clientY: number): void;
  dragTo(clientX: number, clientY: number): void;
  dragEnd(): void;
  fling(vx: number, vy: number): void;
}

/**
 * The hub's gestures on the canvas (ADR 0004: the one gesture source): a pinch (or the desktop's wheel / trackpad
 * pinch) zooms about the fingers, and once zoomed in a drag pans, within the bounds render/hubZoom.ts sets. No turn:
 * the hub's stands are laid out for its fixed 45° view. Taps on the hub's items are DOM buttons over the canvas
 * (the builder chip, the ghost's tag, the pets' targets), which the canvas never sees; `busy` lets the hub swallow a
 * click that lands on one of them during or just after a pinch or a drag, so a pinch never opens a sheet.
 */
export class HubGestures {
  /** Ms after a pinch or a drag during which a click on the hub's buttons is swallowed. */
  static readonly ClickGuardMs = 300;
  private readonly source: GestureSource;
  private guardUntil = -Infinity;

  constructor(canvas: HTMLElement, cam: HubCameraControl, private readonly now: () => number = () => performance.now()) {
    const hold = () => { this.guardUntil = this.now() + HubGestures.ClickGuardMs; };
    this.source = new GestureSource(canvas, {
      pinch: (scale, cx, cy) => { hold(); cam.onPinch(scale, cx, cy); },
      dragBegin: (x, y) => { hold(); cam.dragBegin(x, y); },
      dragMove: (x, y) => { hold(); cam.dragTo(x, y); },
      dragEnd: (_x, _y, vx, vy) => { hold(); cam.dragEnd(); cam.fling(vx, vy); },
      // a two-finger touch began or ended (pinch or not): a click right after it is part of it
      rotateEnd: hold,
    });
  }

  /** Whether a pinch or a drag is under way or just ended (a click now is part of it, not a tap). */
  get busy(): boolean { return this.now() < this.guardUntil; }

  dispose(): void { this.source.dispose(); }
}
